import { useRef, type ReactNode } from 'react'
import './Rows.css'

export { Switch } from './Switch'

/** A card of settings-style rows, 44px each, separated by hairlines. */
export function RowCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`rows ${className}`}>{children}</div>
}

interface RowProps {
  label: ReactNode
  children?: ReactNode
  onClick?: () => void
  ariaPressed?: boolean
  disabled?: boolean
  hint?: string
  tall?: boolean
}

/** One row: a label on the left, a value or control on the right. A button when it acts. */
export function Row({ label, children, onClick, ariaPressed, disabled = false, hint, tall = false }: RowProps) {
  const inner = (
    <>
      <span className="row__label">
        {label}
        {hint && <span className="row__hint">{hint}</span>}
      </span>
      <span className="row__end">{children}</span>
    </>
  )
  if (onClick) {
    return (
      <button type="button" className={`row row--button ${tall ? 'row--tall' : ''}`} onClick={onClick} aria-pressed={ariaPressed} disabled={disabled}>
        {inner}
      </button>
    )
  }
  return <div className={`row ${tall ? 'row--tall' : ''} ${disabled ? 'row--disabled' : ''}`}>{inner}</div>
}

interface DateRowProps {
  label: string
  value: string | null
  placeholder: string
  min?: string
  max?: string
  onChange: (value: string | null) => void
  /** Shows a Clear control when there is a value. */
  clearable?: boolean
  id: string
}

/**
 * Opens a native date or time input's picker from a tap anywhere on its row: on its own the input only
 * answers taps on its text, a small target at the row's end. Taps on the input itself, and on the
 * row's other buttons, are left to them.
 */
function openPicker(input: HTMLInputElement | null, e: React.MouseEvent) {
  const target = e.target as HTMLElement
  if (!input || target === input || target.closest('button')) return
  try {
    input.showPicker()
  } catch {
    // older browsers, or a picker already open: focusing still lets the keyboard pick
    input.focus()
  }
}

/** A row whose value is a native date picker. Empty, it reads the placeholder. The whole row opens it. */
export function DateRow({ label, value, placeholder, min, max, onChange, clearable = true, id }: DateRowProps) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <div className="row row--picks" onClick={(e) => openPicker(input.current, e)}>
      <label className="row__label" htmlFor={id}>
        {label}
      </label>
      <span className="row__end row__picker">
        {!value && <span className="row__ghost">{placeholder}</span>}
        <input
          ref={input}
          id={id}
          type="date"
          className={`row__input ${value ? '' : 'row__input--ghost'}`}
          value={value ?? ''}
          min={min}
          max={max}
          onChange={(e) => onChange(e.target.value || null)}
        />
        {clearable && value && (
          <button type="button" className="row__clear" aria-label={`Clear ${label.toLowerCase()}`} onClick={() => onChange(null)}>
            ×
          </button>
        )}
      </span>
    </div>
  )
}

interface TimeRowProps {
  label: string
  value: string
  onChange: (value: string) => void
  id: string
}

export function TimeRow({ label, value, onChange, id }: TimeRowProps) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <div className="row row--picks" onClick={(e) => openPicker(input.current, e)}>
      <label className="row__label" htmlFor={id}>
        {label}
      </label>
      <span className="row__end row__picker">
        <input ref={input} id={id} type="time" className="row__input row__input--time" value={value} step={300} onChange={(e) => e.target.value && onChange(e.target.value)} />
      </span>
    </div>
  )
}
